export interface CasePrefixIdentity {
  id: string;
  code: string;
}

export interface CaseIdentity {
  caseNumber: string;
  prefix: CasePrefixIdentity;
}

export interface CasePrefixReassignmentOption extends CasePrefixIdentity {
  color: string | null;
  requiresCaseNumberRemainder: boolean;
  defaultBillingScenarioName: string | null;
  isIdentityRepair?: boolean;
  repairCaseNumber?: string;
}

export interface CasePrefixReassignmentContext {
  caseId: string;
  lifecycleStatus: string;
  currentIdentity: CaseIdentity;
  canReassign: boolean;
  blockedReason?: string;
  availablePrefixes: CasePrefixReassignmentOption[];
}

export interface ReassignCasePrefixRequest {
  targetPrefixId: string;
  caseNumberRemainder?: string;
  expectedCurrentCaseNumber: string;
  expectedCurrentPrefixId: string;
  reason: string;
}

export interface ReassignCasePrefixResponse {
  caseId: string;
  previousIdentity: CaseIdentity;
  currentIdentity: CaseIdentity;
  occurredAt: string;
}

export type CasePrefixReassignmentErrorCode =
  | "CASE_PREFIX_REASSIGNMENT_STALE"
  | "CASE_PREFIX_REASSIGNMENT_BLOCKED"
  | "CASE_PREFIX_UNAVAILABLE"
  | "CASE_NUMBER_ALREADY_EXISTS"
  | "INVALID_CANONICAL_CASE_NUMBER"
  | "SAME_CASE_PREFIX";

export interface CasePrefixReassignmentError {
  code: CasePrefixReassignmentErrorCode;
  message: string;
  currentIdentity?: CaseIdentity;
}

const errorCodes = new Set<CasePrefixReassignmentErrorCode>([
  "CASE_PREFIX_REASSIGNMENT_STALE",
  "CASE_PREFIX_REASSIGNMENT_BLOCKED",
  "CASE_PREFIX_UNAVAILABLE",
  "CASE_NUMBER_ALREADY_EXISTS",
  "INVALID_CANONICAL_CASE_NUMBER",
  "SAME_CASE_PREFIX",
]);

export function getCasePrefixReassignmentError(
  error: unknown
): CasePrefixReassignmentError | null {
  if (!error || typeof error !== "object") return null;
  const candidate = error as {
    code?: string;
    message?: string;
    response?: {
      data?: {
        code?: string;
        message?: string;
        currentIdentity?: CaseIdentity;
      };
    };
  };
  const data = candidate.response?.data ?? candidate;
  if (
    !data.code ||
    !errorCodes.has(data.code as CasePrefixReassignmentErrorCode)
  ) {
    return null;
  }
  return data as CasePrefixReassignmentError;
}
